import React, { useState, useEffect } from "react";
import aboutus from "../assets/e-brocure_hero.png";
import brochureCover from "../assets/brochure_general.png";
import useBidirectionalScroll from '../hooks/useBidirectionalScroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

export default function EBrochure({ navigateTo }) {
  useBidirectionalScroll('.ebrochure-page-container', 120);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // ── Static brochure data served locally from public folder ───────────────────
  const catalogs = [
    {
      id: 1,
      title: 'N.D. Tools — Product Catalogue',
      description: 'Complete product catalogue covering all machinery categories including rolling mills, polishing machines, hydraulic presses, melting furnaces and accessories.',
      category: 'General',
      thumbnailUrl: brochureCover,
      fileUrl: '/N.D. TOOLS.pdf'
    },
  ];

  return (
    <div className="ebrochure-page-container"
      style={{
        minHeight: "100vh",
        background: "var(--bg-primary)",
        paddingBottom: "100px"
      }}
    >
      {/* HERO SECTION */}
      <section className="hero" style={{ position: 'relative', overflow: 'hidden', marginBottom: '5rem' }}>
        {/* 3D Interactive Parallax Background Typography */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: 'min(20vw, 250px)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(10, 102, 255, 0.08)',
            letterSpacing: '10px',
            transform: `translate(${mousePos.x * 50}px, ${mousePos.y * 50}px)`,
            transition: 'transform 0.1s ease-out',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            E-Brochure
          </div>
        </div>

        {/* Ambient Grid Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(10, 102, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 102, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
          transition: 'transform 0.15s ease-out',
        }}></div>

        <div className="hero-background-slider" style={{ zIndex: 2, transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)`, transition: 'transform 0.2s ease-out' }}>
          <div className="full-slide active">
            <div className="bg-img-container" style={{ position: 'absolute', inset: 0 }}>
              <img src={aboutus} alt="E-Brochure Header" className="bg-img" style={{ objectPosition: 'right center' }} />
            </div>
            <div className="bg-overlay"></div>
          </div>
        </div>

        <div className="hero-content" style={{ zIndex: 10 }}>
          <button className="outline-button" style={{ marginBottom: '2rem' }} onClick={() => navigateTo('home')}>
            &#8592; Back to Home
          </button>
          <h1 className="hero-title">Digital Catalogues</h1>
          <p className="hero-description">
            <strong style={{ color: "var(--accent-color)" }}>Technical References</strong><br /><br />
            Access our comprehensive technical brochures. Download locally to your device for offline reference and detailed engineering specifications.
          </p>
        </div>
      </section>

      {/* NEW PREMIUM BROCHURE GRID */}
      <div className="ebrochure-grid" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '4rem', padding: '0 6rem', maxWidth: '1440px', margin: '0 auto' }}>
        {catalogs.map((cat, index) => (
          <div key={cat.id || index} className={`brochure-card-premium animate-hidden animate-up delay-${(index % 3) * 100}`} style={{ width: '500px', height: '500px', flexShrink: 0 }}>
            <img 
              src={cat.thumbnailUrl || 'https://via.placeholder.com/500x500?text=Brochure+Cover'} 
              alt={cat.title} 
              className="brochure-card-img" 
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
              onError={(e) => { e.target.src = 'https://via.placeholder.com/500x500?text=No+Cover+Image'; }}
            />

            <div className="brochure-card-overlay">
              <div className="brochure-card-center">
                <a
                  href={cat.fileUrl}
                  download={`${cat.title}.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-brochure-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.9rem' }} />
                  </div>
                  Download Brochure
                </a>
              </div>

              <div className="brochure-card-bottom">
                <h3 className="brochure-card-name">{cat.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', marginTop: '0.3rem' }}>{cat.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', marginTop: '6rem' }}>
        <p style={{ color: 'var(--text-dim)', fontStyle: 'italic', fontSize: '0.9rem' }}>
          *PDF files require a compatible reader. For hard copies, please contact our sales department.
        </p>
      </div>
    </div>
  );
}